/* eslint-disable sonarjs/cognitive-complexity */
import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CImage,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useAppDispatch, useTypedSelector } from '../../stateStore'
import AIMLBridgeLogo from '../../assets/images/logo/ai_bridge_logo_207X65.png'
import RayBizTechLogo from '../../assets/images/logo/raybiztech-logo.png'
import { reduxServices } from '../../reducers/reduxServices'
import { ApiLoadingState } from '../../middleware/api/apiList'

const VendorLogin = (): JSX.Element => {
  const rbtVendorTenantKey = 'RAYBIZTECHR'
  const location = useLocation()
  const containerRef = useRef(null)
  const [tenantKey, setTenantKey] = useState(rbtVendorTenantKey)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [inputCache, setInputCache] = useState({ username: '', password: '' })
  const [hasClickedLogin, setHasClickedLogin] = useState(false)
  const [loginLoader, setLoginLoader] = useState(false)
  const usernameRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  const loading = useTypedSelector(
    reduxServices.authentication.selectors.selectIsLoading,
  )

  const error = useTypedSelector(
    reduxServices.authentication.selectors.selectError,
  )

  const dispatch = useAppDispatch()
  const history = useHistory()

  const state = location.state as {
    from?: string
    username?: string
  }

  useEffect(() => {
    if (state?.username) {
      setUsername(state.username)
      if (state.from === 'vendorResetPassword') {
        setTimeout(() => {
          passwordRef.current?.focus()
        }, 100)
      }
    }
  }, [state])

  // const handleResetPassword = () => {
  //   history.push('/reset-password', {
  //     from: 'vendorLogin',
  //     username,
  //   })
  // }

  // Ref to track if the component is mounted
  const isMountedRef = useRef(true)

  useEffect(() => {
    localStorage.clear()
    sessionStorage.clear()

    return () => {
      // Cleanup to set the ref to false when the component unmounts
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (username !== inputCache.username || password !== '') {
      dispatch(reduxServices.authentication.actions.clearError())
    }
  }, [password, username])

  const rbtCopyright = 'Copyright © Ray Business Technologies Pvt Ltd'

  const logoImg = useMemo(
    () => (tenantKey === rbtVendorTenantKey ? RayBizTechLogo : AIMLBridgeLogo),
    [tenantKey],
  )
  const loginBtnColor = useMemo(
    () => (tenantKey === rbtVendorTenantKey ? 'primary' : 'success'),
    [tenantKey],
  )
  const loginBg = useMemo(
    () => (tenantKey === rbtVendorTenantKey ? 'bg-img' : 'bg-clear'),
    [tenantKey],
  )
  const copyright = useMemo(() => {
    if (tenantKey === rbtVendorTenantKey) return rbtCopyright
    return '' // fallback, optional
  }, [tenantKey])

  const noPassword =
    error !== 401 &&
    error !== 411 &&
    error !== 404 &&
    error !== 500 &&
    hasClickedLogin &&
    !password &&
    !!username
  const noUsername =
    error !== 401 &&
    error !== 411 &&
    error !== 404 &&
    hasClickedLogin &&
    !!password &&
    !username
  const noCredentials =
    error !== 401 &&
    error !== 411 &&
    error !== 404 &&
    hasClickedLogin &&
    !password &&
    !username

  const handleTenantKeyChange = (tenantKey: string) => {
    setTenantKey(tenantKey)
  }

  const login = async () => {
    const startTime = Date.now()
    handleTenantKeyChange('RAYBIZTECHR')
    try {
      setLoginLoader(true)
      const resultAction = await dispatch(
        reduxServices.authentication.authenticateUser({
          username,
          password,
          tenantKey,
        }),
      )

      if (
        reduxServices.authentication.authenticateUser.fulfilled.match(
          resultAction,
        )
      ) {
        const isVendor = localStorage.getItem('isVendor') === 'true'
        dispatch(reduxServices.EmployeeHandbook.actions.setOriginBack('Login'))

        history.push(isVendor ? '/jobvacancies' : '/dashboard')
      } else {
        setPassword('')
        passwordRef.current?.focus()
      }
    } catch (err) {
      console.error(err)
    }
    // Capture the end time
    const endTime = Date.now()
    // Calculate and log the time difference
    const timeDifferenceInSeconds = (endTime - startTime) / 1000
    console.debug(
      `Time taken for authenticateUser: ${timeDifferenceInSeconds} seconds`,
    )
  }

  const handleLogin = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.preventDefault()
    if (!password) {
      passwordRef.current?.focus()
    }
    if (!username && !noUsername) {
      usernameRef.current?.focus()
    }
    setHasClickedLogin(true)
    if (!username || !password) return
    setInputCache({ username, password: '' })
    await login().finally(() => {
      if (isMountedRef.current) {
        setLoginLoader(false)
        dispatch(reduxServices.authentication.actions.clearLoading())
      }
    })
  }

  return (
    <>
      <div
        className={`min-vh-100 align-items-center login-container ${loginBg}`}
      >
        <CContainer fluid ref={containerRef}>
          <CRow className="justify-content-center logo-box">
            <CCol xs="auto">
              <CImage
                src={logoImg}
                alt={tenantKey}
                className="cursor-pointer"
              />
            </CCol>
          </CRow>
          <CRow className="justify-content-center">
            <CCol sm={8} md={4}>
              <CCardGroup>
                <CCard className="login-card">
                  <CCardBody className="login-card-body">
                    <CForm>
                      <h2 className="text-center mg-bot-20 gray-900 login-heading">
                        Login
                      </h2>
                      <CInputGroup className="mb-3">
                        <CInputGroupText className="login-input-icon-container">
                          <i className="fa fa-user"></i>
                        </CInputGroupText>
                        <CFormInput
                          ref={usernameRef}
                          name="username"
                          className="login-input-field"
                          placeholder="Username"
                          autoComplete="username"
                          size="sm"
                          value={username}
                          onChange={(e) => {
                            setHasClickedLogin(false)
                            setUsername(e.target.value)
                          }}
                          disabled={loading === ApiLoadingState.loading}
                          autoFocus={!state?.username}
                        />
                      </CInputGroup>
                      <CInputGroup className="mb-3">
                        <CInputGroupText className="login-input-icon-container">
                          <i className="fa fa-asterisk"></i>
                        </CInputGroupText>
                        <CFormInput
                          ref={passwordRef}
                          name="password"
                          className="login-input-field"
                          type="password"
                          placeholder="Password"
                          autoComplete="current-password"
                          size="sm"
                          value={password}
                          onChange={(e) => {
                            setHasClickedLogin(false)
                            setPassword(e.target.value)
                          }}
                          disabled={loading === ApiLoadingState.loading}
                        />
                      </CInputGroup>
                      {/* <div className="tenant-key">
                        <CFormCheck
                          type="radio"
                          name="tenantKeySelect"
                          value={rbtVendorTenantKey}
                          id="vendorradio"
                          label="Vendor"
                          inline
                          defaultChecked={tenantKey === rbtVendorTenantKey}
                          onChange={handleTenantKeyChange}
                        />
                      </div> */}
                      <CRow>
                        <CCol xs={12} className="d-grid mt-1">
                          <CButton
                            color="primary"
                            data-testid="login-btn"
                            className={`px-4 login-btn-${loginBtnColor}`}
                            disabled={
                              loading === ApiLoadingState.loading || loginLoader
                            }
                            onClick={handleLogin}
                            size="sm"
                            type="submit"
                          >
                            {loading === ApiLoadingState.loading ||
                            loginLoader ? (
                              <CSpinner color="primary" size="sm" />
                            ) : (
                              'Login'
                            )}
                          </CButton>
                          {error === 404 && (
                            <p className="login-error">Login failed</p>
                          )}
                          {(error === 401 ||
                            error === 411 ||
                            error === 500) && (
                            <p className="login-error">
                              Incorrect username or password
                            </p>
                          )}
                          {noCredentials && (
                            <p className="login-error">
                              Username and password is missing.
                            </p>
                          )}
                          {noPassword && (
                            <p className="login-error">Password is missing.</p>
                          )}
                          {noUsername && (
                            <p className="login-error">Username is missing.</p>
                          )}
                        </CCol>
                        {/* <CRow className="mt-2">
                          <CCol xs={12} className="text-center">
                            <span
                              className="cursor-pointer text-primary"
                              style={{ fontSize: '13px' }}
                              onClick={handleResetPassword}
                            >
                              Forgot Password?
                            </span>
                          </CCol>
                        </CRow> */}
                      </CRow>
                      <CRow className="text-center pad-ver login-copyrights">
                        <p>{copyright}</p>
                      </CRow>
                    </CForm>
                  </CCardBody>
                </CCard>
              </CCardGroup>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    </>
  )
}

export default React.memo(VendorLogin)
